import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, BookOpen } from 'lucide-react';
import HelpDot from '@/components/ui/HelpDot';
import { apiFetch } from '@/lib/api';

interface GenreBible {
  genre_key: string;
  display_name?: string;
  summary?: string;
  visual_language?: string;
  camera_grammar?: string;
  editing_grammar?: string;
  pacing?: string;
  wardrobe_world_conventions?: string;
  color_lighting?: string;
  sound_music_conventions?: string;
  title_typography?: string;
  common_tropes?: string[];
  reference_works?: string[];
  do?: string[];
  dont?: string[];
  sources?: string[];
  research_failed?: boolean;
  retired?: boolean;
  source?: string;
  documentary?: boolean;
  wr_principles?: string;
  wr_review_notes?: string;
  wr_synced_at?: string;
  updated_at?: string;
}

const hostOf = (u: string): string => {
  try {
    return new URL(u).hostname.replace('www.', '');
  } catch {
    return u.slice(0, 40);
  }
};

// Only allow http(s) source links to render as clickable — blocks a stored
// `javascript:`/`data:` URI from executing on click (source URLs come from
// stored genre-research data).
const safeHref = (u: string): string | undefined => {
  try {
    const proto = new URL(u).protocol;
    return proto === 'http:' || proto === 'https:' ? u : undefined;
  } catch {
    return undefined;
  }
};

export default function GenreCatalog() {
  const [genres, setGenres] = useState<GenreBible[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [newGenre, setNewGenre] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState('');
  const { t } = useTranslation(['catalog', 'common']);
  const [addError, setAddError] = useState<string | null>(null);

  const load = () => {
    apiFetch('/api/genres')
      .then((r) => r.json())
      .then((data) => setGenres(data.genres || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Writers Room is the SYSTEM OF RECORD for genres: reconcile against it
  // whenever the catalog is opened (adds new WR genres + kicks off their
  // research; retires local-only ones), then again on demand via the button.
  const sync = async (manual = false) => {
    if (manual) setSyncing(true);
    try {
      const r = await apiFetch('/api/genres/sync', { method: 'POST' });
      if (r.ok) {
        const d = await r.json();
        const parts = [
          d.added?.length ? `${d.added.length} added` : '',
          d.retired?.length ? `${d.retired.length} retired` : '',
          d.revived?.length ? `${d.revived.length} revived` : '',
        ].filter(Boolean);
        setSyncNote(parts.length ? t('genre.syncedWith', { summary: parts.join(', ') }) : t('genre.inSync'));
        if (parts.length) load();
      } else {
        const d = await r.json().catch(() => ({}));
        setSyncNote(`Sync failed: ${d.detail || r.status}`);
      }
    } catch {
      setSyncNote(t('genre.syncFailed'));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    load();
    sync();
  }, []);

  const refresh = async (key: string) => {
    setRefreshing(key);
    try {
      await apiFetch(`/api/genres/${key}/refresh`, { method: 'POST' });
      load();
    } finally {
      setRefreshing(null);
    }
  };

  const addGenre = async () => {
    if (!newGenre.trim()) return;
    setAdding(true);
    try {
      const r = await apiFetch('/api/genres', {
        method: 'POST',
        body: JSON.stringify({ name: newGenre.trim() }),
      });
      if (r.ok) {
        setNewGenre('');
        load();
      } else {
        const d = await r.json().catch(() => ({}));
        setAddError(d.detail || t('genre.addFailed'));
      }
    } finally {
      setAdding(false);
    }
  };

  const field = (label: string, value?: string) =>
    value ? (
      <p className="text-sm">
        <span className="font-medium text-muted-foreground">{label}: </span>
        {value}
      </p>
    ) : null;

  return (
    <div className="mx-auto max-w-5xl px-8 py-7">
      <>
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <h2 className="m-0 font-display text-[22px] font-semibold">{t('genre.heading')}</h2>
          <HelpDot>{t('genre.help')}</HelpDot>
          <span className="text-sm text-faint">{t('genre.subtitle')}</span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={() => sync(true)} disabled={syncing}>
            <RefreshCw className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {t('genre.syncFromWR')}
          </Button>
          <input
            className="border rounded px-2 py-1.5 text-sm bg-background w-56"
            placeholder={t('genre.addPlaceholder')}
            value={newGenre}
            onChange={(e) => setNewGenre(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addGenre()}
          />
          <Button size="sm" onClick={addGenre} disabled={adding || !newGenre.trim()}>
            {adding ? t('genre.researching') : t('genre.addResearch')}
          </Button>
        </div>

        {addError && <p className="mb-2 text-xs text-destructive">{addError}</p>}
        {syncNote && (
          <p className="text-xs text-muted-foreground mb-4">
            {syncNote} · {t('genre.systemOfRecord')}
          </p>
        )}

        {loading && <p className="text-muted-foreground">{t('state.loading', { ns: 'common' })}</p>}
        {!loading && genres.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">{t('genre.emptyCatalog')}</CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {[...genres]
            .sort((a, b) => Number(!!a.retired) - Number(!!b.retired))
            .map((g) => (
              <Card key={g.genre_key} className={g.retired ? 'opacity-60' : ''}>
                <CardContent className="py-4 space-y-2">
                  <div
                    className="flex items-center gap-2 cursor-pointer select-none"
                    onClick={() => setOpen((o) => ({ ...o, [g.genre_key]: !o[g.genre_key] }))}
                  >
                    <span className="text-muted-foreground text-xs w-4">{open[g.genre_key] ? '▾' : '▸'}</span>
                    <h3 className="text-lg font-semibold">{g.display_name || g.genre_key}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {g.genre_key}
                    </Badge>
                    {g.retired && (
                      <Badge variant="outline" className="text-xs">
                        {t('genre.retiredBadge')}
                      </Badge>
                    )}
                    {g.source === 'writers_room' && (
                      <Badge variant="outline" className="text-xs">
                        {t('genre.fromWR')}
                      </Badge>
                    )}
                    {g.documentary && (
                      <Badge variant="outline" className="text-xs">
                        {t('genre.documentary')}
                      </Badge>
                    )}
                    {g.research_failed && (
                      <Badge variant="destructive" className="text-xs">
                        {t('genre.researchFailed')}
                      </Badge>
                    )}
                    <div className="flex-1" />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={refreshing === g.genre_key}
                      onClick={(e) => {
                        e.stopPropagation();
                        refresh(g.genre_key);
                      }}
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${refreshing === g.genre_key ? 'animate-spin' : ''}`} />
                      {t('genre.refresh')}
                    </Button>
                  </div>
                  {g.summary && <p className="text-sm text-muted-foreground">{g.summary}</p>}
                  {open[g.genre_key] && (
                    <>
                      {g.wr_principles?.trim() && (
                        <div className="rounded border border-border bg-muted/40 p-2">
                          <p className="text-xs font-medium text-muted-foreground mb-1">{t('genre.principles')}</p>
                          <p className="text-xs whitespace-pre-wrap">{g.wr_principles.trim()}</p>
                        </div>
                      )}
                      {g.wr_review_notes?.trim() && (
                        <div className="rounded border border-border bg-muted/40 p-2">
                          <p className="mb-1 text-xs font-medium text-muted-foreground">{t('genre.reviewNotes')}</p>
                          <p className="text-xs whitespace-pre-wrap">{g.wr_review_notes.trim()}</p>
                        </div>
                      )}
                      {field(t('genre.field.visual_language'), g.visual_language)}
                      {field(t('genre.field.camera'), g.camera_grammar)}
                      {field(t('genre.field.editing'), g.editing_grammar)}
                      {field(t('genre.field.pacing'), g.pacing)}
                      {field(t('genre.field.wardrobe'), g.wardrobe_world_conventions)}
                      {field(t('genre.field.colorLight'), g.color_lighting)}
                      {field(t('genre.field.soundMusic'), g.sound_music_conventions)}
                      {field(t('genre.field.titles'), g.title_typography)}
                      {!!g.common_tropes?.length && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {g.common_tropes.map((t, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {!!g.reference_works?.length && (
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">{t('genre.references')} </span>
                          {g.reference_works.join(' · ')}
                        </p>
                      )}
                      {!!g.sources?.length && (
                        <div className="text-xs text-muted-foreground pt-1">
                          <span className="font-medium">{t('genre.sources')} </span>
                          {g.sources.slice(0, 6).map((s, i) => {
                            const href = safeHref(s);
                            return href ? (
                              <a key={i} href={href} target="_blank" rel="noreferrer" className="underline mr-2 break-all">
                                {hostOf(s)}
                              </a>
                            ) : (
                              <span key={i} className="mr-2 break-all">
                                {hostOf(s)}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
        </div>
      </>
    </div>
  );
}
